/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Directive, ElementRef, Input, Renderer2} from '@angular/core';
import {
  CdkCell,
  CdkCellDef,
  CdkColumnDef,
  CdkHeaderCell,
  CdkHeaderCellDef,
} from '@angular/cdk/table';
/**
 * Workaround for https://github.com/angular/angular/issues/17849
 */
export const _MdCellDef = CdkCellDef;
export const /** @type {?} */ _MdHeaderCellDef = CdkHeaderCellDef;
export const /** @type {?} */ _MdColumnDef = CdkColumnDef;
export const /** @type {?} */ _MdHeaderCell = CdkHeaderCell;
export const /** @type {?} */ _MdCell = CdkCell;
/**
 * Cell definition for the md-table.
 * Captures the template of a column's data row cell as well as cell-specific properties.
 */
export class MdCellDef extends _MdCellDef { static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[mdCellDef], [matCellDef]',
  providers: [{provide: CdkCellDef, useExisting: MdCellDef}]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function MdCellDef_tsickle_Closure_declarations() {
/** @type {?} */
MdCellDef.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdCellDef.ctorParameters;
}

/**
 * Header cell definition for the md-table.
 * Captures the template of a column's header cell and as well as cell-specific properties.
 */
export class MdHeaderCellDef extends _MdHeaderCellDef { static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[mdHeaderCellDef], [matHeaderCellDef]',
  providers: [{provide: CdkHeaderCellDef, useExisting: MdHeaderCellDef}]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function MdHeaderCellDef_tsickle_Closure_declarations() {
/** @type {?} */
MdHeaderCellDef.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdHeaderCellDef.ctorParameters;
}

/**
 * Column definition for the md-table.
 * Defines a set of cells available for a table column.
 */
export class MdColumnDef extends _MdColumnDef {
/**
 * Unique name for this column.
 */
name: string;
/**
 * @return {?}
 */
get _matColumnDefName() { return this.name; }
/**
 * @param {?} name
 * @return {?}
 */
set _matColumnDefName(name) { this.name = name; }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[mdColumnDef], [matColumnDef]',
  providers: [{provide: CdkColumnDef, useExisting: MdColumnDef}],
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'name': [{ type: Input, args: ['mdColumnDef', ] },],
'_matColumnDefName': [{ type: Input, args: ['matColumnDef', ] },],
};
}

function MdColumnDef_tsickle_Closure_declarations() {
/** @type {?} */
MdColumnDef.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdColumnDef.ctorParameters;
/** @type {?} */
MdColumnDef.propDecorators;
/**
 * Unique name for this column.
 * @type {?}
 */
MdColumnDef.prototype.name;
}

/**
 * Header cell template container that adds the right classes and role.
 */
export class MdHeaderCell extends _MdHeaderCell {
/**
 * @param {?} columnDef
 * @param {?} elementRef
 * @param {?} renderer
 */
constructor(columnDef: CdkColumnDef,
              elementRef: ElementRef,
              renderer: Renderer2) {
    super(columnDef, elementRef, renderer);
    renderer.addClass(elementRef.nativeElement, `mat-column-${columnDef.cssClassFriendlyName}`);
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'md-header-cell, mat-header-cell',
  host: {
    'class': 'mat-header-cell',
    'role': 'columnheader',
  },
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: CdkColumnDef, },
{type: ElementRef, },
{type: Renderer2, },
];
}

function MdHeaderCell_tsickle_Closure_declarations() {
/** @type {?} */
MdHeaderCell.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdHeaderCell.ctorParameters;
}

/**
 * Cell template container that adds the right classes and role.
 */
export class MdCell extends _MdCell {
/**
 * @param {?} columnDef
 * @param {?} elementRef
 * @param {?} renderer
 */
constructor(columnDef: CdkColumnDef,
              elementRef: ElementRef,
              renderer: Renderer2) {
    super(columnDef, elementRef, renderer);
    renderer.addClass(elementRef.nativeElement, `mat-column-${columnDef.cssClassFriendlyName}`);
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'md-cell, mat-cell',
  host: {
    'class': 'mat-cell',
    'role': 'gridcell',
  },
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: CdkColumnDef, },
{type: ElementRef, },
{type: Renderer2, },
];
}

function MdCell_tsickle_Closure_declarations() {
/** @type {?} */
MdCell.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdCell.ctorParameters;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
